import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  MapIcon,
  TagIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHART_COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
    purple: 'bg-purple-50 text-purple-700'
  };

  return (
    <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

// Date Range Presets
const DATE_PRESETS = [
  { label: 'This Month', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: start.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
  }},
  { label: 'Last Month', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: start.toISOString().split('T')[0], to: end.toISOString().split('T')[0] };
  }},
  { label: 'This Quarter', getValue: () => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), quarter * 3, 1);
    return { from: start.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
  }},
  { label: 'This Year', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    return { from: start.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
  }},
  { label: 'Last Year', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear() - 1, 0, 1);
    const end = new Date(now.getFullYear() - 1, 11, 31);
    return { from: start.toISOString().split('T')[0], to: end.toISOString().split('T')[0] };
  }},
  { label: 'All Time', getValue: () => ({ from: '', to: '' }) }
];

// Month selector helper
const getMonthPresets = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    months.push({
      label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      value: { from: date.toISOString().split('T')[0], to: endDate.toISOString().split('T')[0] }
    });
  }
  return months;
};

// Default plot colors (matching SiteSettings.js)
const DEFAULT_PLOT_COLORS = {
  noContacts: '#22c55e',
  hasContacts: '#3b82f6',
  hasInterred: '#8b5cf6',
  reserved: '#f59e0b',
  hasVeteran: '#dc2626'
};

export default function Analytics() {
  const { token, siteName, plotColors } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state for unpaid sales table
  const [unpaidPage, setUnpaidPage] = useState(1);
  const unpaidPerPage = 10;

  // Date range state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupBy, setGroupBy] = useState('month');

  // Data state
  const [salesData, setSalesData] = useState(null);
  const [unpaidData, setUnpaidData] = useState(null);
  const [overdueData, setOverdueData] = useState(null);
  const [plotsData, setPlotsData] = useState(null);
  const [contactsData, setContactsData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [workOrdersData, setWorkOrdersData] = useState(null);
  const [stripeData, setStripeData] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch all analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Build query string for sales endpoint
      const salesParams = new URLSearchParams();
      if (dateFrom) salesParams.append('dateFrom', dateFrom);
      if (dateTo) salesParams.append('dateTo', dateTo);
      salesParams.append('groupBy', groupBy);

      // Fetch all data in parallel
      const [salesRes, unpaidRes, overdueRes, plotsRes, contactsRes, productsRes, workOrdersRes] = await Promise.all([
        fetch(`/api/analytics/sales?${salesParams.toString()}`, { headers }),
        fetch('/api/analytics/unpaid', { headers }),
        fetch('/api/analytics/overdue', { headers }),
        fetch('/api/analytics/plots', { headers }),
        fetch('/api/analytics/contacts', { headers }),
        fetch('/api/analytics/products', { headers }),
        fetch('/api/work-orders/analytics', { headers })
      ]);

      if (!salesRes.ok || !unpaidRes.ok || !overdueRes.ok || !plotsRes.ok || !contactsRes.ok || !productsRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const [sales, unpaid, overdue, plots, contacts, products] = await Promise.all([
        salesRes.json(),
        unpaidRes.json(),
        overdueRes.json(),
        plotsRes.json(),
        contactsRes.json(),
        productsRes.json()
      ]);

      setSalesData(sales);
      setUnpaidData(unpaid);
      setOverdueData(overdue);
      setPlotsData(plots);
      setContactsData(contacts);
      setProductsData(products);

      if (workOrdersRes.ok) {
        setWorkOrdersData(await workOrdersRes.json());
      }

      // Fetch Stripe analytics if enabled
      try {
        const stripeStatusRes = await fetch('/api/stripe/status', { headers });
        if (stripeStatusRes.ok) {
          const stripeStatus = await stripeStatusRes.json();
          setStripeEnabled(stripeStatus.enabled);
          if (stripeStatus.enabled) {
            const stripeParams = new URLSearchParams();
            if (dateFrom) stripeParams.append('dateFrom', dateFrom);
            if (dateTo) stripeParams.append('dateTo', dateTo);
            const stripeRes = await fetch(`/api/stripe/analytics?${stripeParams.toString()}`, { headers });
            if (stripeRes.ok) {
              const stripeAnalytics = await stripeRes.json();
              setStripeData(stripeAnalytics.analytics);
            }
          }
        }
      } catch (stripeErr) {
        // Stripe not available - not critical
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, groupBy]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Apply date preset
  const applyDatePreset = (preset) => {
    const { from, to } = preset.getValue();
    setDateFrom(from);
    setDateTo(to);
  };

  // Apply month preset
  const applyMonthPreset = (month) => {
    setDateFrom(month.value.from);
    setDateTo(month.value.to);
  };

  // Export to CSV
  const exportToCSV = (type = 'payments') => {
    if (!salesData) return;

    let csvContent = '';
    let filename = '';

    if (type === 'payments') {
      // Detailed payments export
      csvContent = 'Sale ID,Contact Name,Contact Email,Payment Date,Amount,Payment Type,Note,Sale Date,Sale Total\n';
      for (const payment of salesData.paymentDetails || []) {
        csvContent += `${payment.saleId},"${payment.contactName}","${payment.contactEmail}",${formatDate(payment.paymentDate)},${payment.amount},"${payment.paymentType}","${payment.note || ''}",${formatDate(payment.saleDate)},${payment.saleTotal}\n`;
      }
      filename = `payments_${dateFrom || 'all'}_to_${dateTo || 'present'}.csv`;
    } else if (type === 'quickbooks') {
      // QuickBooks compatible IIF format
      csvContent = '!TRNS\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO\n';
      csvContent += '!SPL\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO\n';
      csvContent += '!ENDTRNS\n';

      for (const payment of salesData.paymentDetails || []) {
        const date = payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('en-US') : '';
        // Transaction line
        csvContent += `TRNS\tDEPOSIT\t${date}\tUndeposited Funds\t"${payment.contactName}"\t${payment.amount}\t"Sale #${payment.saleId}"\n`;
        // Split line (offset to revenue)
        csvContent += `SPL\tDEPOSIT\t${date}\tSales Revenue\t"${payment.contactName}"\t${-payment.amount}\t"Sale #${payment.saleId}"\n`;
        csvContent += `ENDTRNS\n`;
      }
      filename = `quickbooks_payments_${dateFrom || 'all'}_to_${dateTo || 'present'}.iif`;
    } else if (type === 'summary') {
      // Summary export
      csvContent = 'Metric,Value\n';
      csvContent += `Total Sales,${salesData.summary?.totalSales || 0}\n`;
      csvContent += `Total Revenue,${formatCurrency(salesData.summary?.totalRevenue)}\n`;
      csvContent += `Total Paid,${formatCurrency(salesData.summary?.totalPaid)}\n`;
      csvContent += `Total Unpaid,${formatCurrency(salesData.summary?.totalUnpaid)}\n`;
      csvContent += '\nStatus Breakdown\n';
      csvContent += 'Status,Count,Amount\n';
      for (const [status, data] of Object.entries(salesData.statusBreakdown || {})) {
        csvContent += `${status},${data.count},${formatCurrency(data.amount)}\n`;
      }
      filename = `sales_summary_${dateFrom || 'all'}_to_${dateTo || 'present'}.csv`;
    }

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!salesData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(18);
    doc.text(`${siteName} - Payment Report`, pageWidth / 2, 20, { align: 'center' });

    // Date range
    doc.setFontSize(12);
    const dateRangeText = dateFrom || dateTo
      ? `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'Present'}`
      : 'Date Range: All Time';
    doc.text(dateRangeText, pageWidth / 2, 30, { align: 'center' });

    // Summary section
    doc.setFontSize(14);
    doc.text('Summary', 14, 45);

    doc.setFontSize(10);
    const summaryData = [
      ['Total Sales', String(salesData.summary?.totalSales || 0)],
      ['Total Revenue', formatCurrency(salesData.summary?.totalRevenue)],
      ['Total Paid', formatCurrency(salesData.summary?.totalPaid)],
      ['Outstanding Balance', formatCurrency(salesData.summary?.totalUnpaid)]
    ];

    autoTable(doc, {
      startY: 50,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [79, 70, 229] },
      margin: { left: 14, right: 14 }
    });

    // Payments table
    doc.setFontSize(14);
    doc.text('Payment Details', 14, doc.lastAutoTable.finalY + 15);

    const paymentRows = (salesData.paymentDetails || []).map(p => [
      p.saleId,
      p.contactName,
      formatDate(p.paymentDate),
      formatCurrency(p.amount),
      p.paymentType,
      p.note || ''
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Sale ID', 'Contact', 'Date', 'Amount', 'Type', 'Note']],
      body: paymentRows,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8 }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc.save(`payment_report_${dateFrom || 'all'}_to_${dateTo || 'present'}.pdf`);
  };

  // Prepare chart data
  const statusChartData = salesData ? Object.entries(salesData.statusBreakdown || {}).map(([status, data]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
    value: data.count,
    amount: data.amount
  })) : [];

  // Get configured plot colors
  const configuredPlotColors = plotColors || DEFAULT_PLOT_COLORS;

  const plotStatusData = plotsData ? [
    { name: 'Available', value: plotsData.availablePlots || 0, color: configuredPlotColors.noContacts || DEFAULT_PLOT_COLORS.noContacts },
    { name: 'With Owners', value: plotsData.plotsWithOwners || 0, color: configuredPlotColors.hasContacts || DEFAULT_PLOT_COLORS.hasContacts },
    { name: 'With Interred', value: plotsData.plotsWithInterred || 0, color: configuredPlotColors.hasInterred || DEFAULT_PLOT_COLORS.hasInterred },
    { name: 'Reserved', value: plotsData.reservedPlots || 0, color: configuredPlotColors.reserved || DEFAULT_PLOT_COLORS.reserved }
  ].filter(d => d.value > 0) : [];

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'sales', name: 'Sales', icon: CurrencyDollarIcon },
    { id: 'unpaid', name: 'Unpaid/Overdue', icon: ExclamationTriangleIcon },
    ...(stripeEnabled ? [{ id: 'payments', name: 'Online Payments', icon: CurrencyDollarIcon }] : []),
    { id: 'plots', name: 'Parcels', icon: MapIcon },
    { id: 'contacts', name: 'Contacts', icon: UserGroupIcon },
    { id: 'products', name: 'Services', icon: TagIcon },
    { id: 'workorders', name: 'Work Orders', icon: ClipboardDocumentListIcon }
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-100 rounded-lg h-28"></div>
            ))}
          </div>
          <div className="bg-gray-100 rounded-lg h-96"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading analytics: {error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <button
          onClick={fetchAnalytics}
          className="mt-2 sm:mt-0 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                ${activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Contacts"
              value={contactsData?.totalContacts || 0}
              subtitle={`${contactsData?.veteranContacts || 0} veterans`}
              icon={UserGroupIcon}
              color="indigo"
            />
            <StatCard
              title="Total Parcels"
              value={plotsData?.totalPlots || 0}
              subtitle={`${plotsData?.availablePlots || 0} available`}
              icon={MapIcon}
              color="green"
            />
            <StatCard
              title="Unpaid Balances"
              value={unpaidData?.unpaidCount || 0}
              subtitle={formatCurrency(unpaidData?.totalUnpaid)}
              icon={CurrencyDollarIcon}
              color="yellow"
            />
            <StatCard
              title="Overdue Sales"
              value={overdueData?.overdueCount || 0}
              subtitle={formatCurrency(overdueData?.totalOverdue)}
              icon={ExclamationTriangleIcon}
              color="red"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plot Status Chart */}
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Parcel Distribution</h3>
              {plotStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={plotStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {plotStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Parcels']} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No parcel data available
                </div>
              )}
            </div>

            {/* Product Tags Chart */}
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Services by Tag</h3>
              {productsData?.tagBreakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productsData.tagBreakdown.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4F46E5" name="Services" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No service tag data available
                </div>
              )}
            </div>
          </div>

          {/* Veteran Stats */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Veteran Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-2xl font-bold text-indigo-700">{contactsData?.veteranContacts || 0}</p>
                <p className="text-sm text-indigo-600">Total Veterans</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{contactsData?.veteranOwners || 0}</p>
                <p className="text-sm text-green-600">Veteran Owners</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-700">{contactsData?.veteranInterred || 0}</p>
                <p className="text-sm text-purple-600">Veterans Interred</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{plotsData?.veteranOwnership?.plotsWithVeteranInterred || 0}</p>
                <p className="text-sm text-blue-600">Veteran Plots</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          {/* Date Range Filters */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              Date Range
            </h3>

            {/* Quick Presets */}
            <div className="flex flex-wrap gap-2 mb-4">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => applyDatePreset(preset)}
                  className="px-3 py-1.5 text-sm rounded-full border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Month Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Month</label>
              <select
                className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    const month = getMonthPresets().find(m => m.label === e.target.value);
                    if (month) applyMonthPreset(month);
                  }
                }}
                value=""
              >
                <option value="">Select a month...</option>
                {getMonthPresets().map((month) => (
                  <option key={month.label} value={month.label}>{month.label}</option>
                ))}
              </select>
            </div>

            {/* Custom Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sales Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard
              title="Total Sales"
              value={salesData?.summary?.totalSales || 0}
              icon={ChartBarIcon}
              color="indigo"
            />
            <StatCard
              title="Total Revenue"
              value={formatCurrency(salesData?.summary?.totalRevenue)}
              icon={CurrencyDollarIcon}
              color="green"
            />
            <StatCard
              title="Total Paid"
              value={formatCurrency(salesData?.summary?.totalPaid)}
              icon={CurrencyDollarIcon}
              color="blue"
            />
            <StatCard
              title="Outstanding"
              value={formatCurrency(salesData?.summary?.totalUnpaid)}
              icon={ExclamationTriangleIcon}
              color="yellow"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Over Time */}
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Over Time</h3>
              {salesData?.timeSeriesData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={salesData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#4F46E5" name="Revenue" strokeWidth={2} />
                    <Line type="monotone" dataKey="paid" stroke="#10B981" name="Paid" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No data for selected date range
                </div>
              )}
            </div>

            {/* Sales by Status */}
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sales by Status</h3>
              {statusChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [value, `${props.payload.name} (${formatCurrency(props.payload.amount)})`]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No data for selected date range
                </div>
              )}
            </div>
          </div>

          {/* Export Options */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Export Data
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Export payment data for the selected date range. Use these exports for tracking payments and accounting.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => exportToCSV('payments')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export Payments (CSV)
              </button>
              <button
                onClick={() => exportToCSV('summary')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export Summary (CSV)
              </button>
              <button
                onClick={() => exportToCSV('quickbooks')}
                className="inline-flex items-center px-4 py-2 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                QuickBooks Format (IIF)
              </button>
              <button
                onClick={exportToPDF}
                className="inline-flex items-center px-4 py-2 border border-indigo-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export Report (PDF)
              </button>
            </div>
          </div>

          {/* Payments by Contact */}
          {salesData?.paymentsByContact && Object.keys(salesData.paymentsByContact).length > 0 && (
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payments by Contact</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payments</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Paid</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(salesData.paymentsByContact)
                      .sort((a, b) => b[1].total - a[1].total)
                      .slice(0, 10)
                      .map(([contact, data]) => (
                        <tr key={contact}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{contact}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data.payments.length}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(data.total)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Unpaid/Overdue Tab */}
      {activeTab === 'unpaid' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StatCard
              title="Unpaid Balances"
              value={unpaidData?.unpaidCount || 0}
              subtitle={formatCurrency(unpaidData?.totalUnpaid)}
              icon={CurrencyDollarIcon}
              color="yellow"
            />
            <StatCard
              title="Overdue Sales"
              value={overdueData?.overdueCount || 0}
              subtitle={formatCurrency(overdueData?.totalOverdue)}
              icon={ExclamationTriangleIcon}
              color="red"
            />
          </div>

          {/* Unpaid Sales Table */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sales with Unpaid Balances</h3>
            {unpaidData?.unpaidSales?.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {unpaidData.unpaidSales
                        .slice((unpaidPage - 1) * unpaidPerPage, unpaidPage * unpaidPerPage)
                        .map((sale) => (
                          <tr key={sale.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                              <a href={`/sales?search=${sale.id}`}>#{sale.id}</a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sale.contactName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(sale.saleDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(sale.dueDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(sale.total)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(sale.paid)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">{formatCurrency(sale.remaining)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {unpaidData.unpaidSales.length > unpaidPerPage && (
                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                    <div className="text-sm text-gray-500">
                      Showing {((unpaidPage - 1) * unpaidPerPage) + 1} to {Math.min(unpaidPage * unpaidPerPage, unpaidData.unpaidSales.length)} of {unpaidData.unpaidSales.length} results
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setUnpaidPage(p => Math.max(1, p - 1))}
                        disabled={unpaidPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-700">
                        Page {unpaidPage} of {Math.ceil(unpaidData.unpaidSales.length / unpaidPerPage)}
                      </span>
                      <button
                        onClick={() => setUnpaidPage(p => Math.min(Math.ceil(unpaidData.unpaidSales.length / unpaidPerPage), p + 1))}
                        disabled={unpaidPage >= Math.ceil(unpaidData.unpaidSales.length / unpaidPerPage)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500">No unpaid balances found.</p>
            )}
          </div>

          {/* Overdue Sales Table */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Overdue Sales</h3>
            {overdueData?.overdueSales?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Overdue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Due</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {overdueData.overdueSales.map((sale) => (
                      <tr key={sale.id} className={sale.daysOverdue > 30 ? 'bg-red-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                          <a href={`/sales?search=${sale.id}`}>#{sale.id}</a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sale.contactName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(sale.dueDate)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">{sale.daysOverdue} days</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">{formatCurrency(sale.remaining)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No overdue sales found.</p>
            )}
          </div>
        </div>
      )}

      {/* Plots Tab */}
      {activeTab === 'plots' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Parcels"
              value={plotsData?.totalPlots || 0}
              icon={MapIcon}
              color="indigo"
            />
            <StatCard
              title="Available"
              value={plotsData?.availablePlots || 0}
              icon={MapIcon}
              color="green"
            />
            <StatCard
              title="With Owners"
              value={plotsData?.plotsWithOwners || 0}
              icon={UserGroupIcon}
              color="blue"
            />
            <StatCard
              title="With Interred"
              value={plotsData?.plotsWithInterred || 0}
              icon={UserGroupIcon}
              color="purple"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plot Distribution */}
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Parcel Distribution</h3>
              {plotStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={plotStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {plotStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No parcel data available
                </div>
              )}
            </div>

            {/* Veteran Ownership */}
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Veteran Parcel Statistics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-blue-700">Parcels with Veteran Owners</span>
                  <span className="text-lg font-bold text-blue-900">{plotsData?.veteranOwnership?.plotsWithVeteranOwners || 0}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-purple-700">Parcels with Veterans Interred</span>
                  <span className="text-lg font-bold text-purple-900">{plotsData?.veteranOwnership?.plotsWithVeteranInterred || 0}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-green-700">Reserved Parcels</span>
                  <span className="text-lg font-bold text-green-900">{plotsData?.reservedPlots || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Contacts"
              value={contactsData?.totalContacts || 0}
              icon={UserGroupIcon}
              color="indigo"
            />
            <StatCard
              title="Veterans"
              value={contactsData?.veteranContacts || 0}
              subtitle={`${((contactsData?.veteranContacts / contactsData?.totalContacts) * 100 || 0).toFixed(1)}% of total`}
              icon={UserGroupIcon}
              color="blue"
            />
            <StatCard
              title="Owners"
              value={contactsData?.ownerCount || 0}
              icon={UserGroupIcon}
              color="green"
            />
            <StatCard
              title="Interred"
              value={contactsData?.interredCount || 0}
              icon={UserGroupIcon}
              color="purple"
            />
          </div>

          {/* Veteran Breakdown */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Veteran Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-6 bg-indigo-50 rounded-lg">
                <p className="text-3xl font-bold text-indigo-700">{contactsData?.veteranContacts || 0}</p>
                <p className="text-sm text-indigo-600">Total Veterans</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-700">{contactsData?.veteranOwners || 0}</p>
                <p className="text-sm text-green-600">Veteran Owners</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-700">{contactsData?.veteranInterred || 0}</p>
                <p className="text-sm text-purple-600">Veterans Interred</p>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Total Services"
              value={productsData?.totalProducts || 0}
              icon={TagIcon}
              color="indigo"
            />
            <StatCard
              title="Tagged Services"
              value={(productsData?.totalProducts || 0) - (productsData?.untaggedCount || 0)}
              icon={TagIcon}
              color="green"
            />
            <StatCard
              title="Untagged Services"
              value={productsData?.untaggedCount || 0}
              icon={TagIcon}
              color="yellow"
            />
          </div>

          {/* Service Tags Breakdown */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Services by Tag</h3>
            {productsData?.tagBreakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={productsData.tagBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4F46E5" name="Services" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No service tags defined
              </div>
            )}
          </div>

          {/* Top Selling Products */}
          {productsData?.topSellingProducts?.length > 0 && (
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Selling Services</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Count</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Qty</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {productsData.topSellingProducts.map((product, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.salesCount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.totalQty}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(product.totalRevenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Work Orders Tab */}
      {activeTab === 'workorders' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Open Work Orders"
              value={workOrdersData?.openCount || 0}
              icon={ClipboardDocumentListIcon}
              color="blue"
            />
            <StatCard
              title="Overdue"
              value={workOrdersData?.overdueCount || 0}
              icon={ExclamationTriangleIcon}
              color="red"
            />
            <StatCard
              title="Total Statuses"
              value={workOrdersData?.totalStatuses || 0}
              icon={TagIcon}
              color="purple"
            />
          </div>

          {/* Status Breakdown */}
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Work Orders by Status</h3>
            {workOrdersData?.statusCounts?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={workOrdersData.statusCounts.filter(s => s.count > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="status"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {workOrdersData.statusCounts.filter(s => s.count > 0).map((entry, idx) => (
                        <Cell key={idx} fill={entry.color || CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {workOrdersData.statusCounts.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: item.color || CHART_COLORS[idx % CHART_COLORS.length] }} />
                        <span className="text-sm text-gray-700">{item.status}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No work orders found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Online Payments Tab (Stripe) */}
      {activeTab === 'payments' && stripeData && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Collected"
              value={formatCurrency(stripeData.totalPaidAmount)}
              icon={CurrencyDollarIcon}
              color="green"
            />
            <StatCard
              title="Invoices Sent"
              value={stripeData.totalSentCount}
              subtitle={`${stripeData.pendingCount} pending`}
              icon={CurrencyDollarIcon}
              color="blue"
            />
            <StatCard
              title="Email Open Rate"
              value={`${stripeData.emailOpenRate}%`}
              subtitle={`${stripeData.openedCount} of ${stripeData.totalSentCount} opened`}
              icon={CurrencyDollarIcon}
              color="purple"
            />
            <StatCard
              title="Conversion Rate"
              value={`${stripeData.conversionRate}%`}
              subtitle={`${stripeData.totalPaidCount} of ${stripeData.totalSentCount} paid`}
              icon={CurrencyDollarIcon}
              color="indigo"
            />
          </div>

          {/* Monthly Payment Chart */}
          {stripeData.monthlyData?.length > 0 && (
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Online Payments Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stripeData.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="amount" name="Amount Collected" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly Payment Table */}
          {stripeData.monthlyData?.length > 0 && (
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Monthly Breakdown</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payments</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stripeData.monthlyData.map((month) => (
                    <tr key={month.date}>
                      <td className="px-6 py-4 text-sm text-gray-900">{month.date}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">{month.count}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">{formatCurrency(month.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!stripeData.monthlyData?.length && (
            <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-12 text-center">
              <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No online payments yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Online payments will appear here once customers pay via payment links sent from the Sales page.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
